import Alert from '@codegouvfr/react-dsfr/Alert';
import Badge from '@codegouvfr/react-dsfr/Badge';
import Button from '@codegouvfr/react-dsfr/Button';
import ButtonsGroup from '@codegouvfr/react-dsfr/ButtonsGroup';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import { createModal } from '@codegouvfr/react-dsfr/Modal';
import clsx from 'clsx';
import { isEqual, isNil } from 'lodash-es';
import type { Laboratory } from 'maestro-shared/schema/Laboratory/Laboratory';
import { isPPVSubPlan } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import {
  isCreatedPartialSample,
  SampleBase,
  type SampleChecked,
  SampleOwnerData,
  SampleToCreate,
  sampleSendCheck
} from 'maestro-shared/schema/Sample/Sample';
import { isDefined } from 'maestro-shared/utils/utils';
import { checkSchema } from 'maestro-shared/utils/zod';
import type React from 'react';
import {
  type FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link } from 'react-router';
import AppRadioButtons from 'src/components/_app/AppRadioButtons/AppRadioButtons';
import AppTextAreaInput from 'src/components/_app/AppTextAreaInput/AppTextAreaInput';
import AppTextInput from 'src/components/_app/AppTextInput/AppTextInput';
import SupportDocumentSelect from 'src/components/SupportDocumentSelect/SupportDocumentSelect';
import { useForm } from 'src/hooks/useForm';
import { usePartialSample } from 'src/hooks/usePartialSample';
import { useSamplesLink } from 'src/hooks/useSamplesLink';
import { pluralize } from 'src/utils/stringUtils';
import PreviousButton from 'src/views/SampleView/DraftSample/PreviousButton';
import SendingModal from 'src/views/SampleView/DraftSample/SendingStep/SendingModal';
import SavedAlert from 'src/views/SampleView/SavedAlert';
import ContextStepSummary from 'src/views/SampleView/StepSummary/ContextStepSummary';
import ItemsStepSummary from 'src/views/SampleView/StepSummary/ItemsStepSummary';
import MatrixStepSummary from 'src/views/SampleView/StepSummary/MatrixStepSummary';
import { z } from 'zod';
import AppServiceErrorAlert from '../../../../components/_app/AppErrorAlert/AppServiceErrorAlert';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { ApiClientContext } from '../../../../services/apiClient';
import SupportDocumentDownload from '../SupportDocumentDownload';

type Props = {
  sample: (SampleChecked | SampleToCreate) & Partial<SampleOwnerData>;
};

const SendingStep: FunctionComponent<Props> = ({ sample }) => {
  const apiClient = useContext(ApiClientContext);
  const { navigateToSample } = useSamplesLink();
  const { readonly, getSampleItemLaboratory, programmingSubPlan } =
    usePartialSample(sample);
  const { trackEvent } = useAnalytics();

  const isGeolocationEditable = !readonly && isPPVSubPlan(programmingSubPlan);

  const isSubmittingRef = useRef<boolean>(false);

  const [sampledDate, _setSampledDate] = useState(sample.sampledDate);
  const [sampledTime, _setSampledTime] = useState(sample.sampledTime);
  const [sentAt, _setSentAt] = useState(new Date());
  const [resytalId, setResytalId] = useState(sample.resytalId);
  const [ownerFirstName, setOwnerFirstName] = useState(sample.ownerFirstName);
  const [ownerLastName, setOwnerLastName] = useState(sample.ownerLastName);
  const [ownerEmail, setOwnerEmail] = useState(sample.ownerEmail);
  const [ownerAgreement, setOwnerAgreement] = useState(sample.ownerAgreement);
  const [notesOnOwnerAgreement, setNotesOnOwnerAgreement] = useState(
    sample.notesOnOwnerAgreement
  );
  const [specificData, setSpecificData] = useState(sample.specificData);
  const [geolocationX, setGeolocationX] = useState(sample.geolocation?.x);
  const [geolocationY, setGeolocationY] = useState(sample.geolocation?.y);
  const [isSaved, setIsSaved] = useState(false);

  const initialGeolocationRef = useRef(sample.geolocation);
  const hasTrackedGeolocationChangeRef = useRef(false);

  const [createOrUpdateSample, createOrUpdateSampleCall] =
    apiClient.useCreateOrUpdateSampleMutation({
      fixedCacheKey: `sending-sample-${sample.id}`
    });

  const itemsLaboratories = useMemo(
    () =>
      sample.items
        .filter((_) => _.copyNumber === 1)
        .map((item) => ({
          itemNumber: item.itemNumber,
          substanceKinds: item.substanceKinds,
          laboratory: getSampleItemLaboratory(item.itemNumber) as Laboratory
        })),
    [sample.items, getSampleItemLaboratory]
  );

  const hasAllLaboratories = useMemo(
    () => itemsLaboratories.every(({ laboratory }) => isDefined(laboratory)),
    [itemsLaboratories]
  );

  const isSendable = useMemo(
    () =>
      z
        .object({
          ...SampleToCreate.shape,
          ...SampleOwnerData.partial().shape
        })
        .safeParse(sample).success &&
      hasAllLaboratories &&
      (!isGeolocationEditable ||
        (isDefined(geolocationX) && isDefined(geolocationY))),
    [
      sample,
      hasAllLaboratories,
      isGeolocationEditable,
      geolocationX,
      geolocationY
    ]
  );

  const hasSaveError = createOrUpdateSampleCall.isError;

  const sendingSampleModal = useMemo(
    () =>
      createModal({
        id: `sending-sample-modal-${sample.id}`,
        isOpenedByDefault: false
      }),
    [sample.id]
  );

  const FormChecked = checkSchema(
    SampleBase.pick({
      resytalId: true,
      ownerFirstName: true,
      ownerLastName: true,
      ownerEmail: true,
      ownerAgreement: true,
      notesOnOwnerAgreement: true,
      sampledDate: true,
      sentAt: true,
      specificData: true
    })
      .extend({
        geolocationX: z.number({
          error: (issue) => {
            return isNil(issue.input)
              ? 'Veuillez renseigner la latitude.'
              : 'Latitude invalide.';
          }
        }),
        geolocationY: z.number({
          error: (issue) => {
            return isNil(issue.input)
              ? 'Veuillez renseigner la longitude.'
              : 'Longitude invalide.';
          }
        })
      })
      .partial(
        !isGeolocationEditable ? { geolocationX: true, geolocationY: true } : {}
      ),
    sampleSendCheck
  );

  useEffect(() => {
    if (isSubmittingRef.current && !createOrUpdateSampleCall.isLoading) {
      isSubmittingRef.current = false;

      if (createOrUpdateSampleCall.isSuccess) {
        trackEvent('sample', 'submit_Sent', sample.id);
        navigateToSample(sample.id);
      }
    }
  }, [
    createOrUpdateSampleCall.isSuccess,
    createOrUpdateSampleCall.isLoading,
    sample.id
  ]);

  const submit = async () => {
    if (!isSubmittingRef.current) {
      await form.validate(async () => {
        isSubmittingRef.current = true;
        await save('Sent');
      });
    }
  };

  const save = async (step = sample.step) => {
    setIsSaved(false);

    const geolocation =
      isDefined(geolocationX) && isDefined(geolocationY)
        ? { x: geolocationX, y: geolocationY }
        : sample.geolocation;

    if (
      !hasTrackedGeolocationChangeRef.current &&
      !isEqual(geolocation, initialGeolocationRef.current)
    ) {
      hasTrackedGeolocationChangeRef.current = true;
      trackEvent('geolocation', 'update_summary', sample.id);
    }

    await createOrUpdateSample({
      ...sample,
      resytalId,
      ownerFirstName,
      ownerLastName,
      ownerEmail,
      ownerAgreement,
      notesOnOwnerAgreement,
      specificData,
      geolocation,
      step
    });
  };

  const form = useForm(
    FormChecked,
    {
      resytalId,
      ownerFirstName,
      ownerLastName,
      ownerEmail,
      ownerAgreement,
      notesOnOwnerAgreement,
      sampledDate,
      sampledTime,
      sentAt,
      specificData,
      geolocationX,
      geolocationY
    },
    save
  );

  return (
    <>
      <div data-testid="sample_data" className="sample-form">
        <div>
          <Button
            {...PreviousButton({
              sampleId: sample.id,
              currentStep: 4,
              onSave: readonly ? undefined : () => save('DraftItems')
            })}
            size="small"
            priority="tertiary no outline"
            className={cx('fr-pl-0', 'fr-mb-1v')}
          >
            Étape précédente
          </Button>
          <h3 className={cx('fr-m-0')}>
            Récapitulatif du prélèvement{' '}
            {isCreatedPartialSample(sample) && sample.reference}
            {!readonly && (
              <div className={cx('fr-text--md', 'fr-text--regular', 'fr-m-0')}>
                Vérifiez l’ensemble des informations avant de finaliser votre
                envoi
              </div>
            )}
          </h3>
        </div>
        <hr />
        <ContextStepSummary
          sample={sample}
          onChangeResytalId={setResytalId}
          geolocationX={geolocationX}
          geolocationY={geolocationY}
          onChangeLocation={
            isGeolocationEditable
              ? (location) => {
                  setGeolocationX(location.x);
                  setGeolocationY(location.y);
                }
              : undefined
          }
          onChangeGeolocationX={
            isGeolocationEditable ? setGeolocationX : undefined
          }
          onChangeGeolocationY={
            isGeolocationEditable ? setGeolocationY : undefined
          }
          geolocationForm={isGeolocationEditable ? form : undefined}
          onEdit={
            !readonly
              ? async () => {
                  await save('Draft');
                  navigateToSample(sample.id, 1);
                }
              : undefined
          }
        />
        <hr className={cx('fr-mx-0', 'fr-hidden', 'fr-unhidden-sm')} />
        <MatrixStepSummary
          sample={sample}
          onEdit={
            !readonly
              ? async () => {
                  await save('DraftMatrix');
                  navigateToSample(sample.id, 2);
                }
              : undefined
          }
          onUpdateSpecificData={setSpecificData}
        />
        <hr className={cx('fr-mx-0', 'fr-hidden', 'fr-unhidden-sm')} />
        <ItemsStepSummary
          sample={sample}
          onEdit={
            !readonly
              ? async () => {
                  await save('DraftItems');
                  navigateToSample(sample.id, 3);
                }
              : undefined
          }
        />
        <div className={clsx('border', cx('fr-p-5w'))}>
          <div>
            <h5 className={cx('fr-m-0')}>Consentement par le détenteur</h5>
            <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
              <div className={cx('fr-col-12')}>
                <AppRadioButtons
                  legend="Le détenteur accepte les informations portées au présent procès-verbal"
                  options={[
                    {
                      label: 'Oui',
                      nativeInputProps: {
                        checked: ownerAgreement,
                        onChange: () => setOwnerAgreement(true)
                      }
                    },
                    {
                      label: 'Non',
                      nativeInputProps: {
                        checked: isDefined(ownerAgreement) && !ownerAgreement,
                        onChange: () => setOwnerAgreement(false)
                      }
                    }
                  ]}
                  inputForm={form}
                  inputKey="ownerAgreement"
                  required
                  disabled={readonly}
                />
              </div>
            </div>
            <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
              <div className={cx('fr-col-12')}>
                <AppTextAreaInput
                  defaultValue={notesOnOwnerAgreement ?? ''}
                  onChange={(e) => setNotesOnOwnerAgreement(e.target.value)}
                  inputForm={form}
                  inputKey="notesOnOwnerAgreement"
                  whenValid="Déclaration correctement renseignée."
                  label="Déclaration du détenteur"
                  hintText="Champ facultatif pour spécifier une éventuelle déclaration du détenteur"
                  disabled={readonly}
                />
              </div>
            </div>
          </div>
          <hr
            className={cx('fr-mx-0', 'fr-my-5w', 'fr-hidden', 'fr-unhidden-sm')}
          />
          <div>
            <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
              <div className={cx('fr-col-12', 'fr-mb-1w')}>
                <h5 className={cx('fr-mb-0')}>
                  Envoi du compte rendu / procès verbal au détenteur
                </h5>
                {sample.items.length}{' '}
                {pluralize(sample.items.length)('document')}
              </div>
            </div>
            <div
              className={cx('fr-grid-row', 'fr-grid-row--gutters')}
              style={{ alignItems: 'end' }}
            >
              <div className={cx('fr-col-6', 'fr-col-sm-3')}>
                <AppTextInput
                  value={ownerLastName ?? ''}
                  onChange={(e) => setOwnerLastName(e.target.value)}
                  inputForm={form}
                  inputKey="ownerLastName"
                  whenValid="Nom valide"
                  label="Identité du détenteur"
                  hintText="Nom"
                  disabled={readonly}
                />
              </div>
              <div className={cx('fr-col-6', 'fr-col-sm-3')}>
                <AppTextInput
                  value={ownerFirstName ?? ''}
                  onChange={(e) => setOwnerFirstName(e.target.value)}
                  inputForm={form}
                  inputKey="ownerFirstName"
                  whenValid="Prénom valide"
                  hintText="Prénom"
                  disabled={readonly}
                />
              </div>
              <div className={cx('fr-col-12', 'fr-col-sm-6')}>
                <AppTextInput
                  value={ownerEmail ?? ''}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  type="email"
                  inputForm={form}
                  inputKey="ownerEmail"
                  whenValid="Email valide"
                  label="E-mail du détenteur"
                  hintText="Le détenteur recevra une copie du procès-verbal"
                  disabled={readonly}
                />
              </div>
            </div>
          </div>
        </div>

        <h5 className={cx('fr-m-0')}>Documents relatifs au prélèvement</h5>
        <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
          <div className={cx('fr-col-12')}>
            <SupportDocumentSelect
              label="Compte rendu du prélèvement / Procès-verbal"
              sample={sample}
              renderButtons={(onClick) => (
                <ButtonsGroup
                  inlineLayoutWhen="always"
                  buttons={[
                    {
                      children: 'Aperçu',
                      iconId: 'fr-icon-external-link-line',
                      priority: 'secondary',
                      className: cx('fr-mb-0'),
                      onClick
                    }
                  ]}
                />
              )}
            />
          </div>
        </div>
        {!readonly && (
          <div
            className={clsx('d-flex-row', 'd-flex-align-center')}
            style={{ gap: '2rem' }}
          >
            <span className={cx('fr-text--lg', 'fr-m-0')}>
              Étiquettes d'envoi
            </span>
            <Badge severity={'new'} noIcon={true}>
              À joindre aux échantillons
            </Badge>
            <div className={cx('fr-ml-auto')}>
              <SupportDocumentDownload partialSample={sample} alignRight />
            </div>
          </div>
        )}

        {!isSendable && (
          <Alert
            severity="warning"
            description={
              hasAllLaboratories ? (
                <>
                  Certaines informations n’ont pas été renseignées (
                  <b>entité contrôlée</b> et
                  <b> localisation de la parcelle</b>)
                  <div>
                    <Link
                      to=""
                      onClick={async (e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault();
                        await save('Draft');
                        navigateToSample(sample.id, 1);
                      }}
                    >
                      Compléter ces informations
                    </Link>
                  </div>
                </>
              ) : (
                'Le laboratoire destinataire de certaines analyses n’a pas été renseigné lors de la programmation.'
              )
            }
            small
          />
        )}
        <AppServiceErrorAlert call={createOrUpdateSampleCall} />
        {form.hasIssue('sampledDate') && (
          <Alert
            severity="error"
            description={form.message('sampledDate') as string}
            small
            className={cx('fr-mb-4w')}
          />
        )}
        {!createOrUpdateSampleCall.isError && <hr className={cx('fr-mx-0')} />}

        {!readonly && isSendable && (
          <Alert
            severity="warning"
            small
            description="Merci de bien vérifier l’exactitude des informations saisies. Aucune modification ne sera possible après l’envoi de la demande d’analyse."
          />
        )}
        <div className="sample-actions">
          <ul
            className={cx(
              'fr-btns-group',
              'fr-btns-group--inline-md',
              'fr-btns-group--between',
              'fr-btns-group--icon-left'
            )}
          >
            <li>
              <ButtonsGroup
                alignment="left"
                inlineLayoutWhen="md and up"
                buttons={
                  !readonly
                    ? [
                        PreviousButton({
                          sampleId: sample.id,
                          onSave: async () => save('DraftItems'),
                          currentStep: 4
                        }),
                        {
                          children: 'Enregistrer',
                          iconId: 'fr-icon-save-line',
                          priority: 'tertiary',
                          onClick: async (e: React.MouseEvent<HTMLElement>) => {
                            e.preventDefault();
                            await save(sample.step);
                            setIsSaved(true);
                          }
                        }
                      ]
                    : [
                        PreviousButton({
                          sampleId: sample.id,
                          currentStep: 4
                        })
                      ]
                }
              />
            </li>
            <li>
              {!sample.items.some(
                (_) =>
                  _.copyNumber === 1 &&
                  isNil(getSampleItemLaboratory(_.itemNumber))
              ) &&
                !readonly && (
                  <Button
                    iconId="fr-icon-send-plane-fill"
                    iconPosition="right"
                    priority="primary"
                    onClick={async () => {
                      await form.validate(async () =>
                        sendingSampleModal.open()
                      );
                    }}
                    disabled={!isSendable || hasSaveError}
                  >
                    Envoyer la demande d’analyse
                  </Button>
                )}
            </li>
          </ul>
        </div>
        {!!form.error && (
          <Alert
            severity={'error'}
            small={true}
            title={''}
            description={
              form.error.issues.map((i) => i.message).join(',') ?? ''
            }
          />
        )}
        <SavedAlert isOpen={isSaved} sample={sample} />
      </div>
      {!readonly && isSendable && !hasSaveError && (
        <SendingModal
          modal={sendingSampleModal}
          itemsLaboratories={itemsLaboratories}
          programmingSubPlanNumber={programmingSubPlan?.subPlanNumber}
          onConfirm={submit}
        />
      )}
    </>
  );
};

export default SendingStep;
